import axios, { type InternalAxiosRequestConfig } from 'axios';
import { env } from '@/shared/config/env';
import { useAuthStore } from '@/shared/auth/useAuthStore';
import { refreshTokens, refreshGuestTokens } from '@/shared/auth/cognito';
import { useLoadingStore } from './loadingStore';
import { useNotificationStore } from './notificationStore';

export const axiosClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 15000,
});

// Singleton refresh promise — all concurrent requests share one Cognito refresh call
let refreshPromise: Promise<{ id_token: string; access_token: string; expires_in: number }> | null = null;

export async function ensureFreshToken(): Promise<string | null> {
  const { idToken, expiresAt, refreshToken, authClient, user, setTokens, clear } = useAuthStore.getState();
  if (!idToken || !refreshToken) return idToken;

  // Proactively refresh within 90s of expiry so polls never hit an expired token
  const needsRefresh = !expiresAt || Date.now() > expiresAt - 90_000;
  if (!needsRefresh) return idToken;

  if (!refreshPromise) {
    // Refresh must use the same app client that issued the tokens; fall back
    // to the user role for sessions persisted before authClient existed
    const isGuest = authClient ? authClient === 'guest' : user?.role === 'GUEST';
    const doRefresh = isGuest ? refreshGuestTokens : refreshTokens;
    refreshPromise = doRefresh(refreshToken)
      .then((tokens) => { setTokens({ idToken: tokens.id_token, accessToken: tokens.access_token, expiresIn: tokens.expires_in }); return tokens; })
      .catch((err) => { clear(); throw err; })
      .finally(() => { refreshPromise = null; });
  }

  const tokens = await refreshPromise;
  return tokens.id_token;
}

axiosClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await ensureFreshToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  useLoadingStore.getState().increment();
  return config;
});

axiosClient.interceptors.response.use(
  (response) => {
    useLoadingStore.getState().decrement();
    return response;
  },
  async (error) => {
    useLoadingStore.getState().decrement();

    // 401 safety net — proactive refresh should prevent these, but handle just in case
    if (error?.response?.status === 401) {
      const { refreshToken, clear } = useAuthStore.getState();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cfg = error.config as any;
      if (refreshToken && !cfg?._retried) {
        try {
          const token = await ensureFreshToken();
          if (!token) { clear(); return Promise.reject(error); }
          const retryConfig = { ...cfg, _retried: true };
          retryConfig.headers = { ...retryConfig.headers, Authorization: `Bearer ${token}` };
          return axiosClient(retryConfig);
        } catch {
          clear();
        }
      }
      return Promise.reject(error);
    }

    const status: number | undefined = error?.response?.status;

    // 401s are handled above (redirect to login) — never show a toast for them
    if (status === 401) return Promise.reject(error);

    const message: string =
      error?.response?.data?.error?.message ??
      fallbackMessage(status);
    const severity = status && status >= 500 ? 'error' : 'warning';
    const title = errorTitle(status);

    useNotificationStore.getState().show(message, severity, title);
    return Promise.reject(error);
  },
);

function errorTitle(status?: number): string {
  if (status === 400) return 'Bad Request';
  if (status === 403) return 'Access Forbidden';
  if (status === 404) return 'Not Found';
  if (status === 409) return 'Conflict';
  if (status === 422) return 'Validation Error';
  if (status && status >= 500) return 'Server Error';
  return 'Something Went Wrong';
}

function fallbackMessage(status?: number): string {
  if (status === 400) return 'The request was invalid or malformed';
  if (status === 403) return 'You do not have permission to perform this action';
  if (status === 404) return 'The requested resource was not found';
  if (status === 409) return 'This action conflicts with existing data';
  if (status === 422) return 'The submitted data failed validation';
  if (status && status >= 500) return 'A server error occurred, please try again';
  return 'An unexpected error occurred';
}
